import { isDisplayable }        from '../../../shared/utils/productUtils.js'
import { AVAILABILITY_STATUS }  from '../../../shared/constants/availabilityStatus.js'
/**
 * Derives the display-useful tier list from buildings and products data.
 *
 *   1. Collect all (building_id, ref_tier) pairs on which any product has a
 *      tier entry (i.e. a compartment exists on that floor).
 *   2. Per building, find the highest occupied ref_tier.
 *   3. Keep all building tiers with ref_tier <= that maximum — so ground floor
 *      and basements are included even if they have no apartments, but purely
 *      technical top floors (roof, service level) are excluded.
 *
 * Buildings with no products are omitted entirely.
 *
 * @param {object[]} buildings  - array from buildings.json → `.buildings`
 * @param {object[]} products   - array of normalized product objects
 * @param {object[]} tiersSettings   - tiers array with cms panel settings
 * @returns {{ ref_tier: number, name_original: string, id_cad: string, building_id: string }[]}
 */
export function buildTiersData(buildings, products, tiersSettings) {
  if (!buildings?.length || !products?.length) return []

  // Step 1: collect (building_id, ref_tier) pairs that have at least one product tier
  const occupiedKeys = new Set()
  const availableKeys = new Set()

  for (const product of products) {
    if (!product.id_cad || !product.tiers?.length) continue

    const buildingId = product.id_cad.split('-')[0]

    const isAvailableOrReserved = 
          isDisplayable(product.visibility) && 
          product.status !== AVAILABILITY_STATUS.SOLD && 
          product.status !== AVAILABILITY_STATUS.HOLD

    for (const tier of product.tiers) {
      if (tier.ref_tier != null) {
        const key = `${buildingId}||${tier.ref_tier}`

        occupiedKeys.add(key)
        
        if (isAvailableOrReserved) {
          availableKeys.add(key)
        }      }
    }
  }

  // Step 2: per building, find the maximum occupied ref_tier
  const buildingMaxTier = new Map()
  for (const building of buildings) {
    let max = -Infinity
    for (const tier of (building.tiers ?? [])) {
      if (occupiedKeys.has(`${building.id_cad}||${tier.ref_tier}`)) {
        if (tier.ref_tier > max) max = tier.ref_tier
      }
    }
    if (max !== -Infinity) {
      buildingMaxTier.set(building.id_cad, max)
    }
  }

  // Step 3: keep all building tiers <= max occupied tier
  const occupiedTiers = []
  for (const building of buildings) {
    const max = buildingMaxTier.get(building.id_cad)
    if (max === undefined) continue // no products for this building → skip
    
    for (const tier of (building.tiers ?? [])) {
      if (tier.ref_tier <= max) {
        const tierKey = `${building.id_cad}||${tier.ref_tier}`
        const isSoldOut = occupiedKeys.has(tierKey) && !availableKeys.has(tierKey)

        occupiedTiers.push({
          ref_tier:       tier.ref_tier,
          name_original:  tier.name_original,
          id:             `${building.id_cad}-${tier.id_cad}`, //use this or remove? id is used in panelSettings
          id_cad:         `${building.id_cad}-${tier.id_cad}`, //maybe id_cad should only be for apartment?
          id_cad_tier:    tier.id_cad, //when to char third part of id_cad
          building_id:    building.id_cad,
          isSoldOut,
        })
      }
    }
  }

  // Step 4: Join tier objects with panelSettings
  // TOOD: decide - remove not active tiers from results or later use `is_active` condition?
  // TODO: change keys to camelCase to separate them from JSON keys
  const settingsMap = new Map();

  for ( const setting of tiersSettings ) {
    if (setting.id) {
      settingsMap.set(setting.id, {
        is_active: setting.is_active,
        cms_tier_name: setting.cms_tier_name,
        cms_tier_name_short: setting.cms_tier_name_short,
      })
    }
  }

  const result = [];

  for ( const tier of occupiedTiers ) {
    const compositeKeyFallback = `${tier.building_id}-${tier.id_cad_tier}`
    
    const matchedSetting =
      (tier.id && settingsMap.get(tier.id)) ||
      (tier.id_cad && settingsMap.get(tier.id_cad)) ||
      settingsMap.get(compositeKeyFallback) ||
      {}

    result.push({
      ...tier,
      isActive: matchedSetting?.is_active ?? true,
      is_active: matchedSetting?.is_active ?? true,
      cms_tier_name: matchedSetting?.cms_tier_name || "",
      cms_tier_name_short: matchedSetting?.cms_tier_name_short || "",
    })
  }

  return result
}
